import React, { useEffect, useRef, useState, useCallback } from 'react'
import styles from './Chat.module.scss'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import {
  HeaderAddButton,
  SuperButton,
} from 'components/UI/SuperButton/SuperButton'
import useLocalization from 'hooks/useLocalization'
import ChatHeader from './ChatHeader/ChatHeader'
import ChatMessageList from './ChatMessageList/ChatMessageList'
import ChatInput from './ChatInput/ChatInput'
import asideModalStore from 'Stores/asideModalStore'
import BackButton from 'components/CRM/UI/components/BackButton/BackButton'
import useWindowWidth from 'components/CRM/utils/hooks/useWindowWidth'
import { superAxiosRequest } from 'axiosApi'
import AddNewTask from 'components/ContentBox/Global/GlobalModalSingle/templates/CRM/AddNewTask/AddNewTask'
import PinMessageHeader from './PinMessageHeader/PinMessageHeader'
import SearchBar from 'components/CRM/UI/components/SearchBar/SearchBar'
import PageVisitTracker from '../../UI/components/PageVisitTracker/PageVisitTracker'
import Stores from 'Stores'
import ChatTabs from './ChatTabs/ChatTabs'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import { addZero } from 'helpers/math'
import { dateFormat } from 'helpers/date'

const Chat = ({
  isClose,
  setIsClose,
  dealData,
  dealId,
  setDealData,
  fetchDealData,
  funnelId,
}) => {
  const [messages, setMessages] = useState([])
  const [pinnedMessages, setPinnedMessages] = useState([])
  const [channelId, setChannelId] = useState(null)
  const [inputText, setInputText] = useState('')
  const [messageStatus, setMessageStatus] = useState(null)
  const [newMessageId, setNewMessageId] = useState(null)
  const [pinPage, setPinPage] = useState(false)
  const messageRefs = useRef({})
  const [shouldScroll, setShouldScroll] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [taskStatuses, setTaskStatuses] = useState([])
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [disableBtn, setDisableBtn] = useState(false)

  const [wazzupIframeUrl, setWazzupIframeUrl] = useState(null)
  const [isWazzupLoading, setIsWazzupLoading] = useState(false)

  // состояния для управления табами
  const [activeDealId, setActiveDealId] = useState(dealId)
  const [activeDealData, setActiveDealData] = useState(dealData)
  const [combinedDealsData, setCombinedDealsData] = useState({})

  // Рефы для WebSocket соединений
  const wsChatRef = useRef(null) // Для communication_sessions
  const wsDealsRef = useRef(null) // Для deals активной сделки
  const isMountedRef = useRef(false)

  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 768

  const scrollToMessage = (messageId) => {
    const messageRef = messageRefs.current[messageId]

    if (messageRef) {
      messageRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
  }

  useEffect(() => {
    setPinnedMessages(
      messages.filter((message) => message?.data?.is_pinned === true),
    )
  }, [messages])

  // Функция для переключения таба
  const handleTabChange = async (newDealId) => {
    if (newDealId === activeDealId) return

    setIsLoadingChat(true)

    let newDealData
    if (newDealId === dealData?.id) {
      newDealData = dealData
    } else if (combinedDealsData[newDealId]) {
      newDealData = combinedDealsData[newDealId]
    } else {
      // Загружаем данные объединенной сделки
      try {
        const apiConf = {
          method: 'get',
          link: `integration/deals/${newDealId}`,
          crm: true,
        }

        const res = await superAxiosRequest(apiConf)
        newDealData = res.data
        setCombinedDealsData((prev) => ({
          ...prev,
          [newDealId]: res.data,
        }))
      } catch (error) {
        setIsLoadingChat(false)
        return
      }
    }

    setActiveDealId(newDealId)
    setActiveDealData(newDealData)

    fetchMessages(newDealId).finally(() => {
      setIsLoadingChat(false)
    })
  }

  // Функция для закрытия WebSocket соединений
  const closeWebSocketConnections = useCallback(() => {
    if (wsChatRef.current) {
      wsChatRef.current.close()
      wsChatRef.current = null
    }

    if (wsDealsRef.current) {
      wsDealsRef.current.close()
      wsDealsRef.current = null
    }
  }, [])

  // Функция для загрузки сообщений с использованием deal_id
  const fetchMessages = useCallback((dealIdToFetch = activeDealId) => {
    console.log(dealIdToFetch)
    if (!dealIdToFetch) {
      setMessages([])
      return Promise.resolve()
    }

    const apiConf = {
      method: 'get',
      link: `integration/messages`,
      crm: true,
      params: {
        deal_id: dealIdToFetch,
        limit: 999,
      },
    }

    return superAxiosRequest(apiConf)
      .then((res) => {
        setMessages(res.data || [])
      })
      .catch((err) => {
        console.log('Ошибка при загрузке сообщений:', err)
        setMessages([])
      })
  }, [])

  // Основной эффект для управления WebSocket соединениями
  useEffect(() => {
    // Закрываем предыдущие соединения только если компонент уже монтирован
    if (isMountedRef.current) {
      closeWebSocketConnections()
    }

    isMountedRef.current = true

    // Создаем новые соединения если есть данные
    if (activeDealId && activeDealData) {
      // 1. WebSocket для сессии чата (communication_sessions)
      if (activeDealData?.session_id) {
        const chatLink = 'communication_sessions'
        const chatWsUrl = `${Stores.baseLinkCRM().replace('https', 'wss')}/${chatLink}/${activeDealData.session_id}/?token=${localStorage.getItem('access')}`

        wsChatRef.current = new WebSocket(chatWsUrl)

        wsChatRef.current.onmessage = (e) => {
          try {
            const message = JSON.parse(e.data)

            switch (message.type) {
              case 'new_message':
                setNewMessageId(message.data?.data?.id)
                setMessages((prevMessages) => [...prevMessages, message.data])
                setMessageStatus(message.data?.data?.sending_status)
                break
              case 'update_message_sending_status':
                const updatedId = message.data?.data?.id
                const newStatus = message.data?.data?.sending_status

                if (updatedId && newStatus !== undefined) {
                  setMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                      msg.data.id === updatedId
                        ? {
                            ...msg,
                            data: { ...msg.data, sending_status: newStatus },
                          }
                        : msg,
                    ),
                  )
                }

                setNewMessageId(message.data?.data?.id)
                setMessageStatus(message.data?.data?.sending_status)
                break
              case 'edit_message':
                handleEditMessage(message.data)
                break
              case 'delete_message':
                handleDeleteMessage(message.data)
                break
              default:
                break
            }
          } catch (error) {
            console.error('Ошибка обработки WebSocket сообщения:', error)
          }
        }

        wsChatRef.current.onerror = (error) => {
          // console.error('WebSocket error for communication_sessions:', error)
        }

        wsChatRef.current.onclose = () => {
          // console.log('WebSocket connection closed for communication_sessions')
        }
      }

      // 2. WebSocket для событий сделки (deals)
      const dealsLink = 'deals'
      const dealsWsUrl = `${Stores.baseLinkCRM().replace('https', 'wss')}/${dealsLink}/${activeDealId}/?token=${localStorage.getItem('access')}`

      wsDealsRef.current = new WebSocket(dealsWsUrl)

      wsDealsRef.current.onmessage = (e) => {
        try {
          const message = JSON.parse(e.data)

          switch (message.type) {
            case 'new_call':
              setMessages((prevMessages) => [...prevMessages, message.data])

              break
            case 'new_task':
              setMessages((prevMessages) => [...prevMessages, message.data])
              break
            default:
              break
          }
        } catch (error) {
          console.error('Ошибка обработки WebSocket сообщения deals:', error)
        }
      }

      wsDealsRef.current.onerror = (error) => {
        // console.error('WebSocket error for deals:', error)
      }

      wsDealsRef.current.onclose = () => {
        // console.log('WebSocket connection closed for deals')
      }
    }

    // Функция очистки при размонтировании компонента
    return () => {
      closeWebSocketConnections()
      isMountedRef.current = false
    }
  }, [activeDealId, activeDealData?.session_id, closeWebSocketConnections])

  const handleEditMessage = (updatedMessage) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.data?.id === updatedMessage.data?.id ? updatedMessage : msg,
      ),
    )

    setPinnedMessages((prevPinned) =>
      prevPinned.map((msg) =>
        msg.data?.id === updatedMessage.data?.id ? updatedMessage : msg,
      ),
    )
  }

  const handleDeleteMessage = (deletedMessage) => {
    setMessages((prevMessages) =>
      prevMessages.filter((msg) => msg.data?.id !== deletedMessage.data?.id),
    )

    setPinnedMessages((prevPinned) =>
      prevPinned.filter((msg) => msg.data?.id !== deletedMessage.data?.id),
    )
  }

  const fetchTaskStatuses = async () => {
    try {
      const res = await superAxiosRequest({
        method: 'get',
        link: 'tasktracker/task_statuses',
        crm: true,
      })
      setTaskStatuses(res.data.result || [])
    } catch (error) {
      console.error('Ошибка при загрузке статусов задач:', error)
    }
  }

  useEffect(() => {
    fetchTaskStatuses()
  }, [])

  // Функция отправки сообщения
  const isSendingRef = useRef(false)
  const pendingMessageRef = useRef(null)

  const sendMessage = () => {
    if (
      !activeDealId ||
      !inputText.trim() ||
      disableBtn ||
      isSendingRef.current
    ) {
      return
    }

    if (pendingMessageRef.current === inputText.trim()) {
      return
    }

    isSendingRef.current = true
    pendingMessageRef.current = inputText.trim()
    setDisableBtn(true)

    const messageText = inputText
    setInputText('')

    const requestData = {
      deal_id: activeDealId,
      text: messageText,
      type_message: 'text',
    }

    if (activeDealData?.session_id) {
      requestData.session_id = activeDealData.session_id
    }

    if (channelId) {
      requestData.channel_id = channelId
    }

    superAxiosRequest(
      {
        link: `integration/messages`,
        method: 'post',
        crm: true,
      },
      requestData,
    )
      .then(() => {
        pendingMessageRef.current = null
        setTimeout(() => fetchMessages(activeDealId), 500)
      })
      .catch((error) => {
        console.error('Ошибка при отправке сообщения:', error)
        pendingMessageRef.current = null
        setInputText(messageText)
      })
      .finally(() => {
        isSendingRef.current = false
        setDisableBtn(false)
      })
  }

  // Функция добавления заметки
  const addNote = () => {
    if (activeDealId !== dealData?.id) {
      console.log(
        'Добавление заметки для объединенной сделки не поддерживается',
      )
      setInputText('')
      return
    }

    superAxiosRequest(
      {
        link: `integration/deals/${dealId}`,
        method: 'put',
        crm: true,
      },
      {
        deal_name: dealData?.deal_name,
        manager: dealData.manager?.id,
        client: dealData.client?.id,
        budget: dealData.budget,
        city: dealData.city?.id,
        branch: dealData.branch?.id,
        program: dealData.program?.id,
        level: dealData.level?.id,
        status_deal: dealData.status_deal?.id,
        reject_reason: dealData?.reject_reason,
        funnels: [funnelId],
        note: inputText,
      },
    )
      .then((res) => {
        setInputText('')
        setDealData((prevData) => ({
          ...prevData,
          note: res?.data?.note,
        }))
        // Обновляем данные активной сделки
        setActiveDealData((prev) => ({
          ...prev,
          note: res?.data?.note,
        }))
      })
      .catch((error) => {
        console.error('Ошибка при добавлении заметки:', error)
      })
  }

  const onBeforeAddTask = (formData) => {
    const { date, start_at, finish_at } = formData

    const defaultStatusId = taskStatuses.find(
      (status) => status.name === 'Новая',
    )?.id

    const result = {
      ...formData,
      status: formData.status ?? defaultStatusId,
      creator: formData?.assignee,
    }

    const startTime = start_at || '06:00:00'
    const endTime = finish_at || '23:00:00'

    result.start_task = new Date(`${date}T${startTime}`).toISOString()
    result.finish_task = new Date(`${date}T${endTime}`).toISOString()

    delete result.start_at
    delete result.finish_at
    delete result.date

    return result
  }

  const handleOpenWazzup = async () => {
    const wazzupUserId =
        activeDealData?.manager?.wazzup_user_id ||
        dealData?.manager?.wazzup_user_id

    const allDeals = [dealData, ...(dealData?.combined_deals || [])]
    const chatIdsSet = new Set()

    allDeals.forEach((deal) => {
      const client = deal?.client || deal?.lead?.client
      const rawPhone = client?.whatsapp || client?.phone

      if (rawPhone) {
        const cleanPhone = String(rawPhone).replace(/^\+/, '')
        chatIdsSet.add(cleanPhone)
      }
    })

    const chat_ids = Array.from(chatIdsSet)

    if (!wazzupUserId || chat_ids.length === 0) {
      console.warn('Не хватает данных для открытия Wazzup!')
      return
    }

    setIsWazzupLoading(true)
    try {
      const res = await superAxiosRequest(
          {
            method: 'post',
            link: 'integration/wazzup_iframe',
            crm: true,
          },
          {
            wazzup_user_id: wazzupUserId,
            chat_ids: chat_ids, // Отправляем массив номеров
          },
      )

      if (res.data?.url) {
        setWazzupIframeUrl(res.data.url)
      }
    } catch (error) {
      console.error('Ошибка при загрузке Wazzup iframe:', error)
    } finally {
      setIsWazzupLoading(false)
    }
  }
  // Функция добавления задачи
  const addTask = () => {
    const ms = {
      modalBodyComponent: () => <AddNewTask addTaskFromDeal />,
      crm: true,
      button: translate.save,
      link: 'tasktracker/tasks',
      title: translate.add_task,
      onBeforeSubmit: onBeforeAddTask,
      onSubmitSuccess: (res) => {
        fetchMessages(activeDealId)
      },
      isResponsive: true,
      valid: {
        task_category: 'required',
        assignee: 'required',
        date: 'required',
        deal: 'required',
      },
    }
    const md = {
      deal: activeDealId,
      manager: activeDealData?.manager?.id || dealData?.manager?.id,
    }
    asideModalStore.activity(ms, md)
  }

  // Функция редактирования задачи
  const editTask = (task) => {
    // Функция для извлечения времени из даты
    const getTimeFromDate = (dateString) => {
      if (!dateString) return '06:00:00'
      const date = new Date(dateString)
      return `${addZero(date.getHours())}:${addZero(date.getMinutes())}:${addZero(date.getSeconds())}`
    }

    const ms = {
      modalBodyComponent: () => <AddNewTask addTaskFromDeal />,
      crm: true,
      is_put: true,
      button: translate.save,
      link: `tasktracker/tasks/${task?.id}`,
      title: 'Редактировать задачу',
      onBeforeSubmit: (formData) => {
        const { date, start_at, finish_at } = formData

        const result = {
          task_category: formData.task_category,
          assignee: formData.assignee,
          deal: activeDealId,
          comment: formData.comment,
          status: formData.status,
        }

        // Формируем start_task и finish_task из date и времени
        const startTime = start_at || '06:00:00'
        const endTime = finish_at || '23:00:00'

        result.start_task = new Date(`${date}T${startTime}`).toISOString()
        result.finish_task = new Date(`${date}T${endTime}`).toISOString()

        return result
      },
      onSubmitSuccess: (res) => {
        fetchMessages(activeDealId)
        asideModalStore.close()
      },
      isResponsive: true,
      valid: {
        task_category: 'required',
        assignee: 'required',
        date: 'required',
        deal: 'required',
      },
    }

    // Подготавливаем данные для формы
    const taskDateObj = new Date(task.finish_task || new Date())
    const formattedDate = dateFormat(taskDateObj, 'YYYY-mm-dd')
    const startTime = task.start_task
      ? getTimeFromDate(task.start_task)
      : '06:00:00'
    const endTime = task.finish_task
      ? getTimeFromDate(task.finish_task)
      : '23:00:00'

    const md = {
      deal: activeDealId,
      assignee: task.assignee?.id,
      task_category: task.task_category?.id,
      date: formattedDate,
      start_at: startTime,
      finish_at: endTime,
      comment: task.comment || '',
      status: task.status?.id,
    }

    asideModalStore.activity(ms, md)
  }

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'all_groups',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',

      application_from_site: 'Application from the site',
      deal_history: 'Deal history',
      add_task: 'Add a task',
      search_placeholder: 'Search',
      working_on_card: 'Working on a card',

      unpin_one_message: 'Unpin 1 message',
      unpin_few_messages: 'Unpin {count} messages',
      unpin_many_messages: 'Unpin {count} messages',

      save: 'Save',
    },
  })

  const getUnpinText = (count) => {
    if (count === 1) {
      return translate.unpin_one_message || 'Unpin 1 message'
    } else if (count >= 2 && count <= 4) {
      return (
        translate.unpin_few_messages?.replace('{count}', count) ||
        `Unpin ${count} messages`
      )
    } else {
      return (
        translate.unpin_many_messages?.replace('{count}', count) ||
        `Unpin ${count} messages`
      )
    }
  }

  const unpinText = getUnpinText(pinnedMessages.length)

  const unpinAllMessages = () => {
    const messageIds = pinnedMessages
      .filter((message) => message.type === 'message')
      .map((message) => message?.data?.id)
    const taskIds = pinnedMessages
      .filter((message) => message.type === 'task')
      .map((message) => message?.data?.id)

    superAxiosRequest(
      {
        link: `integration/messages/pin`,
        method: 'post',
        crm: true,
      },
      {
        message_ids: messageIds,
        task_ids: taskIds,
      },
    )
      .then((res) => {
        if (pinPage) {
          setMessages((prevMessages) => {
            return prevMessages.map((message) => {
              const updatedItem = res?.data.find(
                (item) => item?.data?.id === message.data.id,
              )
              if (updatedItem) {
                return updatedItem
              } else {
                return message
              }
            })
          })
        }
        setShouldScroll(true)
        setPinPage(false)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const dealSource =
    activeDealData?.lead?.source?.name || dealData?.lead?.source?.name

  // Проверяем, есть ли объединенные сделки
  const mainClientId = dealData?.client?.id || dealData?.lead?.client?.id
  const hasCombinedDeals = dealData?.combined_deals?.some((combinedDeal) => {
    const combinedClient = combinedDeal.client || combinedDeal.lead?.client
    return combinedClient && combinedClient.id !== mainClientId
  })

  // При монтировании компонента, устанавливаем активной главную сделку
  useEffect(() => {
    if (dealData?.id) {
      setActiveDealId(dealData.id)
      setActiveDealData(dealData)
      fetchMessages(dealData.id)
    }
  }, [dealData?.id])

  return (
    <div
      className={`${styles.container} ${!isClose.chat && isMobile ? styles.closeChat : ''}`}
    >
      <div className={styles.filterAndTracker}>
        <div className={styles.filterHeader}>
          <div className={styles.headerContainer}>
            {isMobile && (
              <BackButton
                type={'arrow'}
                onClick={() => setIsClose({ chat: false, card: false })}
              />
            )}
            <PageHeader
              title={
                dealSource
                  ? `Источник заявки: ${dealSource}`
                  : translate?.application_from_site
              }
              // subtitle={dealSource && `Источник: ${dealSource}`}
            >
              <div style={{ display: 'flex', gap: '20px' }}>
                <a
                  rel='noreferrer'
                  className={styles.historyLink}
                  href={`/crm/deal-history/${activeDealId}?funnel_id=${funnelId}`}
                  target={'_blank'}
                >
                  {translate.deal_history}
                </a>
                <HeaderAddButton title={translate.add_task} onClick={addTask} />
              </div>
            </PageHeader>
          </div>

          {/* Отображаем табы только если есть объединенные сделки */}
          {hasCombinedDeals && (
            <div className={styles.chatTabs}>
              <ChatTabs
                dealData={dealData}
                activeDealId={activeDealId}
                onTabChange={handleTabChange}
              />
            </div>
          )}

          {messages.length ? (
            <div className={styles.search}>
              <SearchBar
                onSearch={handleSearch}
                onClear={() => setSearchTerm('')}
                placeholder={`${translate.search}...`}
                translate={translate}
                value={searchTerm || ''}
              />
              {searchTerm && (
                <div className={styles.searchTextValue}>
                  <SuperButton
                    variant='filter'
                    isShowCross
                    callback={() => setSearchTerm('')}
                  >
                    Search:&nbsp;<b>{searchTerm}</b>
                  </SuperButton>
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className={styles.tracker}>
          <PageVisitTracker
            page={`funnel_id=${funnelId}?deal_id=${activeDealId}`}
          />
        </div>
      </div>

      <div className={styles.chatContainer}>
        {!pinPage && (
          <ChatHeader
            dealData={activeDealData}
            onOpenWazzup={handleOpenWazzup}
            isWazzupLoading={isWazzupLoading}
            isWazzupOpen={!!wazzupIframeUrl}
            onCloseWazzup={() => setWazzupIframeUrl(null)}
          />
        )}
        {pinnedMessages.length ? (
          <PinMessageHeader
            setShouldScroll={setShouldScroll}
            pinPage={pinPage}
            setPinPage={setPinPage}
            fetchMessages={() => fetchMessages(activeDealId)}
            pinnedMessages={pinnedMessages}
            scrollToMessage={scrollToMessage}
          />
        ) : null}
        {wazzupIframeUrl ? (
          <div className={styles.wazzupIframeWrapper}>
            <iframe
              src={wazzupIframeUrl}
              allow='microphone *; clipboard-write *'
              className={styles.wazzupIframe}
              title='Wazzup Chat'
            />
          </div>
        ) : (
          <>
            {isLoadingChat ? (
              <Loader />
            ) : (
              <ChatMessageList
                fetchDealData={fetchDealData}
                shouldScroll={shouldScroll}
                setShouldScroll={setShouldScroll}
                pinPage={pinPage}
                setPinPage={setPinPage}
                dealId={activeDealId}
                dealData={activeDealData}
                setDealData={(data) => {
                  if (activeDealId === dealData?.id) {
                    setDealData(data)
                  } else {
                    setCombinedDealsData((prev) => ({
                      ...prev,
                      [activeDealId]: data,
                    }))
                  }
                  setActiveDealData(data)
                }}
                messages={pinPage ? pinnedMessages : messages}
                setMessages={setMessages}
                fetchMessages={() => fetchMessages(activeDealId)}
                messageStatus={messageStatus}
                newMessageId={newMessageId}
                messageRefs={messageRefs}
                searchTerm={searchTerm}
                funnelId={funnelId}
                editTask={editTask}
                sessionId={activeDealData?.session_id}
                channelId={channelId}
              />
            )}

            {pinPage ? (
              <div onClick={unpinAllMessages} className={styles.unpinBtn}>
                {unpinText}
              </div>
            ) : (
              <ChatInput
                dealData={activeDealData}
                inputText={inputText}
                setInputText={setInputText}
                sendMessage={sendMessage}
                addNote={addNote}
                disabled={!inputText.trim().length || disableBtn}
                sessionId={activeDealData?.session_id}
                funnelId={funnelId}
                fetchMessages={() => fetchMessages(activeDealId)}
                setChannelId={setChannelId}
                channelId={channelId}
                activeDealId={activeDealId}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Chat
